import './App.css'
import {
  useState,
  useEffect,
  useCallback,
  createContext,
  useContext,
  useMemo
} from 'react'
import { initializeApp } from 'firebase/app'
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset
} from 'firebase/auth'
import {
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation,
  Navigate,
  Outlet
} from 'react-router-dom'
import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import CloseIcon from '@mui/icons-material/Close'
import Alert from '@mui/material/Alert'
import Toolbar from '@mui/material/Toolbar'
import AppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import Logout from '@mui/icons-material/Logout'
import LightModeIcon from '@mui/icons-material/LightModeOutlined'
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined'
import BrightnessAutoIcon from '@mui/icons-material/BrightnessAutoOutlined'
import Tooltip from '@mui/material/Tooltip'
import { useScrollTrigger } from '@mui/material'
import { Image } from '@imagekit/react'
import { ASSETS } from './config/imagekit'
import Home from './routes/home'
import SendReset from 'routes/sendReset'
import Form from 'routes/form'
import Reset from 'routes/reset'
import CodeHandler from 'routes/codeHandler'

type ThemeMode = 'light' | 'dark' | 'auto'
type ResolvedMode = 'light' | 'dark'
const THEME_STORAGE_KEY = 'voxist:themeMode'

const ThemeModeContext = createContext<{
  mode: ThemeMode
  resolved: ResolvedMode
  toggleMode: () => void
}>({ mode: 'auto', resolved: 'light', toggleMode: () => {} })

const useThemeMode = () => useContext(ThemeModeContext)

const readInitialMode = (): ThemeMode => {
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (saved === 'light' || saved === 'dark' || saved === 'auto')
      return saved
  } catch {}
  return 'auto'
}

const systemPrefersDark = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches

const AuthContext = createContext(null)

const AlertContext = createContext(null)

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const navigate = useNavigate()
  const { setAlert } = useAlert()
  auth.languageCode = 'fr'

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (userOb) => {
      if (userOb) {
        // console.log(userOb)
        setUser(userOb)
        setAlert({ type: 'success', text: 'logged in' })
      } else {
        setUser(null)
      }
    })
    return () => {
      unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getToken = async () => {
    try {
      const token = await auth.currentUser.getIdToken()
      return token
    } catch (error) {
      setAlert({ type: 'error', text: 'no user' })
    }
  }
  const sendReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email)
      navigate('/')
    } catch (error) {
      setAlert({ type: 'error', text: 'issue with firebase' })
    }
  }
  const resetPass = async (oob, password) => {
    try {
      await confirmPasswordReset(auth, oob, password)
      navigate('/')
    } catch (error) {
      setAlert({ type: 'error', text: 'issue with firebase' })
    }
  }

  const signIn = async (email, password) => {
    try {
      return await signInWithEmailAndPassword(auth, email, password)
    } catch (error) {
      setAlert({ type: 'error', text: 'bad login' })
    }
  }

  const signOut = async () => {
    await auth.signOut()
  }

  const value = { user, signIn, signOut, getToken, sendReset, resetPass }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const useAuth = () => useContext(AuthContext)

const useQuery = () => {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}
const RequireAuth = ({ children }) => {
  const auth = useAuth()
  const location = useLocation()

  if (!auth.user) {
    return <Navigate to="/" state={{ from: location }} replace />
  }

  return children
}

const CheckAuth = ({ children }) => {
  const auth = useAuth()
  const location = useLocation()

  if (auth.user) {
    return <Navigate to="/form" state={{ from: location }} replace />
  }

  return children
}

const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState(null)
  const value = { alert, setAlert }

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
}

const useAlert = () => useContext(AlertContext)

const Layout = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const theme = useTheme()
  const { alert, setAlert } = useAlert()
  const { mode, toggleMode } = useThemeMode()
  const iconColor = theme.palette.mode === 'dark' ? '#FFFFFF' : '#0A0F1C'

  useEffect(() => {
    let timeout = null
    if (alert) {
      timeout = setTimeout(() => setAlert(null), 5000)
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert])

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0
  })

  return (
    <>
      <AppBar position="static" elevation={0} color="transparent">
        <Toolbar
          sx={(t) => {
            const isDark = t.palette.mode === 'dark'
            return {
              backgroundColor: trigger
                ? isDark
                  ? 'rgba(0, 0, 0, 0.72)'
                  : 'rgba(255, 255, 255, 0.72)'
                : 'transparent',
              backdropFilter: trigger ? 'saturate(180%) blur(18px)' : 'none',
              WebkitBackdropFilter: trigger
                ? 'saturate(180%) blur(18px)'
                : 'none',
              borderBottom: trigger
                ? `1px solid ${isDark ? '#1F1F22' : '#E5E5EA'}`
                : '1px solid transparent',
              transition:
                'background-color 180ms ease, border-color 180ms ease',
              top: 0,
              position: 'fixed',
              zIndex: 1,
              right: 0,
              left: 0,
              alignItems: 'center',
              minHeight: { xs: 56, sm: 64 }
            }
          }}
        >
          {location?.pathname !== '/' && (
            <Tooltip title="Home">
              <Link
                to="/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  textDecoration: 'none'
                }}
              >
                <Image
                  src={ASSETS.logoVoxist}
                  alt="Voxist"
                  width={32}
                  height={32}
                  responsive={false}
                />
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    lineHeight: 1.1
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '0.9375rem',
                      fontWeight: 700,
                      letterSpacing: '-0.01em',
                      color: iconColor
                    }}
                  >
                    Voxist
                  </Box>
                  <Box
                    sx={(t) => ({
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: t.palette.mode === 'dark' ? '#98989D' : '#6E6E73'
                    })}
                  >
                    Support
                  </Box>
                </Box>
              </Link>
            </Tooltip>
          )}
          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            {auth?.user?.email && (
              <Box
                sx={(t) => ({
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 0.75,
                  height: 32,
                  px: 1.25,
                  borderRadius: '999px',
                  backgroundColor:
                    t.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F5',
                  border: `1px solid ${
                    t.palette.mode === 'dark' ? '#2A2A2E' : '#E5E5EA'
                  }`,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color:
                    t.palette.mode === 'dark' ? '#F5F5F7' : '#0A0F1C',
                  maxWidth: { xs: 160, sm: 240 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                })}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#30D158',
                    flexShrink: 0
                  }}
                />
                {auth.user.email}
              </Box>
            )}
            <Tooltip
              title={
                mode === 'light'
                  ? 'Light · click for Dark'
                  : mode === 'dark'
                  ? 'Dark · click for Auto'
                  : 'Auto (system) · click for Light'
              }
            >
              <IconButton
                aria-label={
                  mode === 'light'
                    ? 'Switch to dark mode'
                    : mode === 'dark'
                    ? 'Switch to auto mode'
                    : 'Switch to light mode'
                }
                onClick={toggleMode}
                size="medium"
                sx={{ color: iconColor }}
              >
                {mode === 'light' ? (
                  <LightModeIcon fontSize="small" />
                ) : mode === 'dark' ? (
                  <DarkModeIcon fontSize="small" />
                ) : (
                  <BrightnessAutoIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {auth?.user && (
              <Tooltip title="Log out">
                <IconButton
                  size="medium"
                  aria-label="logout"
                  onClick={() => {
                    auth.signOut(() => navigate('/'))
                  }}
                  sx={{ color: iconColor }}
                >
                  <Logout fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Outlet />
      <Box
        sx={{
          bottom: 0,
          position: 'fixed',
          right: 0,
          left: { xs: 0, sm: 'auto' },
          zIndex: 2,
          px: { xs: 1, sm: 2 },
          pb: { xs: 1, sm: 2 }
        }}
      >
        <Fade in={Boolean(alert)} unmountOnExit>
          <Alert
            severity={alert?.type}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setAlert(null)
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 2 }}
          >
            {alert?.text}
          </Alert>
        </Fade>
      </Box>
    </>
  )
}

const firebaseConfig = {
  apiKey: 'AIzaSyDiOOI7uVMjtaTRKbrB7fxob-w3jbVUV5A',
  authDomain: 'voxist-f2e51.firebaseapp.com',
  databaseURL: 'https://voxist-f2e51.firebaseio.com',
  projectId: 'voxist-f2e51',
  storageBucket: 'voxist-f2e51.appspot.com',
  messagingSenderId: '1044982363144',
  appId: '1:1044982363144:web:1549e9cc099cd40d5fcdbd',
  measurementId: 'G-9F9E4WZJH7'
}

const app = initializeApp(firebaseConfig)

const auth = getAuth(app)

export { useAuth, useQuery, useAlert }

const App = () => {
  const [mode, setMode] = useState<ThemeMode>(readInitialMode)
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark)

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, mode)
    } catch {}
  }, [mode])

  useEffect(() => {
    if (mode !== 'auto') return
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches)
    setSystemDark(mql.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [mode])

  const resolved: ResolvedMode =
    mode === 'auto' ? (systemDark ? 'dark' : 'light') : mode

  const toggleMode = useCallback(() => {
    setMode((m) =>
      m === 'light' ? 'dark' : m === 'dark' ? 'auto' : 'light'
    )
  }, [])

  const themeModeValue = useMemo(
    () => ({ mode, resolved, toggleMode }),
    [mode, resolved, toggleMode]
  )

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily:
            '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontWeightLight: 300,
          fontWeightRegular: 400,
          fontWeightMedium: 700,
          fontWeightBold: 700
        },
        components: {
          MuiInputBase: {
            styleOverrides: {
              input: {
                '&:-webkit-autofill': {
                  transitionDelay: '9999s',
                  transitionProperty: 'background-color, color'
                }
              }
            }
          },
          MuiFilledInput: {
            styleOverrides: {
              input: {
                '&:-webkit-autofill': {
                  WebkitBoxShadow: 'inherit',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                  borderRadius: 'inherit'
                }
              }
            }
          },
          MuiOutlinedInput: {
            styleOverrides: {
              input: {
                '&:-webkit-autofill': {
                  WebkitBoxShadow: 'inherit',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                  borderRadius: 'inherit'
                }
              }
            }
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                background: 'transparent'
              },
              colorDefault: {
                background: 'transparent'
              }
            }
          },
          MuiToolbar: {
            styleOverrides: {
              root: {
                background: 'transparent'
              }
            }
          }
        },
        palette: {
          mode: resolved,
          ...(resolved === 'dark'
            ? {
                background: {
                  default: '#000000',
                  paper: '#121212'
                }
              }
            : {
                background: {
                  default: '#ffffff',
                  paper: '#f8f8f8'
                }
              })
        }
      }),
    [resolved]
  )
  return (
    <ThemeModeContext.Provider value={themeModeValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AlertProvider>
          <AuthProvider>
            <Routes>
            <Route element={<Layout />}>
              <Route
                path="/"
                element={
                  <CheckAuth>
                    <Home />
                  </CheckAuth>
                }
              />
              <Route
                path="/resetLink"
                element={
                  <CheckAuth>
                    <SendReset />
                  </CheckAuth>
                }
              />
              <Route
                path="/reset"
                element={
                  <CheckAuth>
                    <Reset />
                  </CheckAuth>
                }
              />
              <Route
                path="/codeHandler"
                element={
                  <CheckAuth>
                    <CodeHandler />
                  </CheckAuth>
                }
              />

              <Route
                path="/form"
                element={
                  <RequireAuth>
                    <Form />
                  </RequireAuth>
                }
              />
            </Route>
          </Routes>
          </AuthProvider>
        </AlertProvider>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export default App
